import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { BookOpen, Edit, Eye, Fingerprint, Trash2, type LucideIcon } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

function ActionTooltip({ label, children }: { label: string; children: ReactElement }) {
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}

export type CatalogEntityCardMeta = {
  label: string;
  value: string;
  icon: LucideIcon;
  iconClassName?: string;
};

export function formatCatalogAuditDate(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

type CatalogEntityCardProps = {
  title: string;
  /** Optional icon shown before the title (e.g. person for customers). */
  titleIcon?: LucideIcon;
  titleIconClassName?: string;
  /** Optional employee-style photo; when set, replaces the title icon. */
  avatarUrl?: string | null;
  /** Small pill on the right of the title row (e.g. Active / Inactive). */
  statusBadge?: { label: string; className: string };
  primaryMeta: CatalogEntityCardMeta;
  secondaryMeta?: CatalogEntityCardMeta;
  /** Meta grid columns. Defaults to 2. */
  metaColumns?: 1 | 2;
  /** When omitted (and no `footerRight`), the footer strip is hidden. */
  footer?: ReactNode;
  footerRight?: ReactNode;
  footerRightClassName?: string;
  /** When omitted, the edit button is not shown. */
  onEdit?: () => void;
  /** Icon-only edit button (no "Edit" label). */
  editIconOnly?: boolean;
  /** When set, renders a ledger icon button after Edit. */
  onLedger?: () => void;
  /** When omitted, the delete button is not shown (e.g. system roles). */
  onDelete?: () => void;
  /** When set, renders a View button before Edit (e.g. employees). */
  onView?: () => void;
  /** Icon-only view button (no "View" label). */
  viewIconOnly?: boolean;
  /** Optional biometric enroll action (employees). */
  onConnectDevice?: () => void;
  connectDeviceLoading?: boolean;
  connectDeviceDisabled?: boolean;
  extraActions?: ReactNode;
  className?: string;
  /** When true, title/meta wrap fully instead of truncating with ellipsis. */
  wrapText?: boolean;
};

function MetaCell({
  label,
  value,
  icon: Icon,
  iconClassName,
  wrapText = false,
}: CatalogEntityCardMeta & { wrapText?: boolean }) {
  return (
    <div className="flex gap-2.5 items-start min-w-0">
      <Icon className={cn("w-4 h-4 shrink-0 mt-0.5", iconClassName ?? "text-emerald-600")} strokeWidth={2} />
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
        <div
          className={cn(
            "font-semibold text-sm text-foreground",
            wrapText ? "break-words whitespace-normal" : "truncate",
          )}
          title={wrapText ? undefined : value}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

export function CatalogEntityCard({
  title,
  titleIcon: TitleIcon,
  titleIconClassName,
  avatarUrl,
  statusBadge,
  primaryMeta,
  secondaryMeta,
  metaColumns = 2,
  footer = null,
  footerRight,
  footerRightClassName,
  onEdit,
  editIconOnly = false,
  onLedger,
  onDelete,
  onView,
  viewIconOnly = false,
  onConnectDevice,
  connectDeviceLoading = false,
  connectDeviceDisabled = false,
  extraActions,
  className,
  wrapText = false,
}: CatalogEntityCardProps) {
  const showFooter =
    (footer != null && footer !== "") || (footerRight != null && footerRight !== "");

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-card text-card-foreground overflow-hidden shadow-sm",
        className,
      )}
    >
      <div className="h-1 w-full bg-primary" aria-hidden />
      <div className="p-4 pt-5 h-full flex flex-col gap-4">
        <div className="space-y-4 min-h-0 flex-1">
          <div className="flex items-start gap-2.5 min-w-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="w-10 h-10 rounded-full object-cover shrink-0 border border-border mt-0.5"
              />
            ) : TitleIcon ? (
              <TitleIcon
                className={cn("w-5 h-5 shrink-0 text-primary mt-0.5", titleIconClassName)}
                strokeWidth={2}
                aria-hidden
              />
            ) : null}
            <h3
              className={cn(
                "text-lg font-bold uppercase tracking-tight text-foreground leading-snug min-w-0 flex-1",
                wrapText ? "break-words whitespace-normal" : "truncate whitespace-nowrap",
              )}
            >
              {title}
            </h3>
            {statusBadge ? (
              <span
                className={cn(
                  "shrink-0 mt-1 text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap",
                  statusBadge.className,
                )}
              >
                {statusBadge.label}
              </span>
            ) : null}
          </div>
          <div
            className={cn(
              "grid gap-x-3 gap-y-3",
              metaColumns === 1 || !secondaryMeta ? "grid-cols-1" : "grid-cols-2",
            )}
          >
            <MetaCell {...primaryMeta} wrapText={wrapText} />
            {secondaryMeta ? <MetaCell {...secondaryMeta} wrapText={wrapText} /> : null}
          </div>
          {showFooter ? (
            <div className="border-t border-border pt-3">
              {footerRight ? (
                <div className="flex items-start justify-between gap-3 text-xs text-muted-foreground leading-relaxed">
                  <div className={cn("min-w-0 flex-1", wrapText && "break-words")}>{footer}</div>
                  <div className={cn("shrink-0 text-right", footerRightClassName)}>{footerRight}</div>
                </div>
              ) : (
                <div className={cn("text-xs text-muted-foreground leading-relaxed", wrapText && "break-words")}>
                  {footer}
                </div>
              )}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2 mt-auto">
          {extraActions}
          {onConnectDevice ? (
            <ActionTooltip
              label={connectDeviceLoading ? "Syncing…" : connectDeviceDisabled ? "Synced" : "Sync device"}
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-2 h-9 px-3 text-xs font-medium"
                onClick={onConnectDevice}
                disabled={connectDeviceLoading || connectDeviceDisabled}
              >
                <Fingerprint className="w-3.5 h-3.5 shrink-0" />
                {connectDeviceLoading ? "Syncing…" : connectDeviceDisabled ? "Synced" : "Sync"}
              </Button>
            </ActionTooltip>
          ) : null}
          {onView ? (
            <ActionTooltip label="View Details">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={
                  viewIconOnly
                    ? "h-9 w-9 shrink-0 p-0"
                    : "shrink-0 gap-2 h-9 px-3 text-xs font-medium"
                }
                onClick={onView}
                aria-label="View Details"
              >
                <Eye className="w-3.5 h-3.5 shrink-0" />
                {viewIconOnly ? null : "View"}
              </Button>
            </ActionTooltip>
          ) : null}
          {onEdit ? (
            <ActionTooltip label="Edit">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={
                  editIconOnly
                    ? "h-9 w-9 shrink-0 p-0"
                    : "shrink-0 gap-2 h-9 px-3 text-xs font-medium"
                }
                onClick={onEdit}
                aria-label="Edit"
              >
                <Edit className="w-3.5 h-3.5 shrink-0" />
                {editIconOnly ? null : "Edit"}
              </Button>
            </ActionTooltip>
          ) : null}
          {onLedger ? (
            <ActionTooltip label="Ledger">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-9 shrink-0 p-0"
                onClick={onLedger}
                aria-label="Ledger"
              >
                <BookOpen className="w-3.5 h-3.5" />
              </Button>
            </ActionTooltip>
          ) : null}
          {onDelete ? (
            <ActionTooltip label="Delete">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 w-9 shrink-0 border-destructive/60 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={onDelete}
                aria-label="Delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </ActionTooltip>
          ) : null}
        </div>
      </div>
    </div>
  );
}
